import { useCallback } from "react";

import { useApiMutation } from "@/api/useApiQuery";
import { saveUserFilter as saveUserFilterUrl } from "@/service/reqUrl.service";

import type { SaveUserFilterParams, UserFilterDetails } from "./types";

interface UseSaveUserFilterResult {
  /** Persist this dashboard's filter selection. Resolves false on failure —
   *  the caller ignores it, since a lost preference write must not disturb a
   *  dashboard that is otherwise showing correct data. */
  saveUserFilter: (
    dashboardID: number | string,
    details: UserFilterDetails,
  ) => Promise<boolean>;
}

/**
 * Calls `POST /dashboard/save-user-filter` (`[DayLight].[SaveAppUserPerfs]` with
 * `@PerfCode = 'UserFilter'`) — the SAME row
 * `[DayLight].[GetMetricsValuesForDashboard]` writes as a side effect of
 * returning metric values.
 *
 * ── Why a second writer exists ─────────────────────────────────────────────
 * Saving the filter selection inside the metrics SP makes persistence a side
 * effect of a READ, and a read is exactly what TanStack Query is allowed to
 * skip. Serve a metrics response from cache and the SP never runs, so the
 * stored selection keeps whatever was last actually requested over the wire.
 * The visible symptom is a user clearing every filter — which returns to a
 * combination already in cache — and the dashboard reopening with those filters
 * still applied, because the stored row was never updated.
 *
 * `useDashboardFilterSync` owns the decision of WHEN to call this: only on a
 * cache hit, and only when the selection differs from what the server is known
 * to hold. On a real network metrics call the SP still does the writing and
 * this hook stays out of the way.
 *
 * ── No invalidation on success ─────────────────────────────────────────────
 * Deliberate. `cacheTiers.prefs` runs at `staleTime: 0`, so `dashboard/custom-views`
 * refetches on every mount regardless — the next time this dashboard is opened
 * it reads the row we just wrote, from the server. Invalidating here would only
 * add a round-trip for a response nothing outstanding is waiting on: the tables
 * sharing that entry read `views`, not `filters`, and the page's own filter
 * hydration is a one-shot that has already run.
 */
export function useSaveUserFilter(): UseSaveUserFilterResult {
  const { run } = useApiMutation<SaveUserFilterParams>({
    url: saveUserFilterUrl,
    fallbackMessage: "Failed to save filters",
    buildBody: (params) => params,
  });

  const saveUserFilter = useCallback(
    (dashboardID: number | string, details: UserFilterDetails): Promise<boolean> =>
      run({ dashboardID, perfDetails: details }),
    [run],
  );

  return { saveUserFilter };
}
